// Length-variance prompt — shown when a delivered take's duration deviates
// from the reference voice by more than the sync_mode tolerance.
//
// HARD tone rules (per friendly_ui_tone.md):
//   - Always presented as a QUESTION with two clear button options.
//     The two paths are "submit anyway" and "re-record".
//   - Intent is inferred from which button the actor clicks. NEVER ask the
//     actor to type a free-text reason here.
//   - Use "参考声" / "voz de referencia" / "reference voice" — minimize the
//     word "AI".
//   - Loose / creative modes return null OR an info-only line. They never
//     prompt for an action.
/** Tolerance window per sync mode, in ms. Prompts only fire when |dev| exceeds. */
export const SYNC_TOLERANCE_MS = {
    strict: 100,
    loose: 500,
    creative: Number.POSITIVE_INFINITY, // never prompts
};
/**
 * Multilingual labels for the two-option button row that always accompanies a
 * length-variance prompt. The LEFT button keeps the take, the RIGHT button
 * triggers a re-record. Intent is inferred from the click — no text input.
 */
export const LENGTH_PROMPT_BUTTONS = {
    submit: {
        icon: '✅',
        zh: '直接提交',
        en: 'Submit anyway',
        es: 'Enviar igualmente',
    },
    rerecord: {
        icon: '🔁',
        zh: '重录 (R)',
        en: 'Re-record (R)',
        es: 'Repetir toma (R)',
    },
};
/**
 * Build the length-variance prompt body.
 *
 * @param deviationMs signed deviation from reference (positive = take is longer)
 * @param syncMode    the cue's sync mode
 * @returns the prompt body, or `null` when no prompt should be shown.
 *          Callers must always render `LENGTH_PROMPT_BUTTONS` when this is
 *          non-null so the actor has both submit and re-record paths.
 */
export function lengthPrompt(deviationMs, syncMode) {
    const abs = Math.abs(Math.round(deviationMs));
    const tolerance = SYNC_TOLERANCE_MS[syncMode];
    // Creative mode never prompts.
    if (syncMode === 'creative')
        return null;
    // Within tolerance — silent accept.
    if (abs <= tolerance)
        return null;
    const longer = deviationMs > 0;
    const direction = {
        zh: longer ? '长' : '短',
        en: longer ? 'longer than' : 'shorter than',
        es: longer ? 'más larga que' : 'más corta que',
    };
    // Strict mode: a real question with two paths.
    if (syncMode === 'strict') {
        return {
            icon: '⏱️',
            zh: `比参考声${direction.zh} ${abs} ms。需要重录 (R) 还是直接提交?`,
            en: `${abs} ms ${direction.en} the reference voice. Re-record (R) or submit?`,
            es: `${abs} ms ${direction.es} la voz de referencia. ¿Repetir la toma (R) o enviar?`,
        };
    }
    // Loose mode: informational only — still rendered as a soft question so the
    // two-button affordance below it makes sense.
    return {
        icon: '⏱️',
        zh: `比参考声${direction.zh} ${abs} ms,在宽松同步可接受范围内。要保留还是重录?`,
        en: `${abs} ms ${direction.en} the reference voice — fine for loose-sync. Keep or re-record?`,
        es: `${abs} ms ${direction.es} la voz de referencia — aceptable para sincronía suelta. ¿Conservar o repetir?`,
    };
}
// ── Variance prompt extras ──────────────────────────────────────────────────
// Used by VariancePrompt.tsx — the modal shown when a take's percent-deviation
// exceeds the project threshold. Three buttons: keep this take as the best,
// re-record, or mark for review (held-button placeholder for now). Tone rules
// apply: questions, not directives; no warning red; minimal "AI" word.
/** Modal title above the variance question. */
export const VARIANCE_TITLE = {
    icon: '⏱️',
    zh: '时长有点偏差',
    en: 'Length is off',
    es: 'La duración difiere',
};
/** "Keep this take as the best" button label. */
export const KEEP_TAKE = {
    icon: '✅',
    zh: '保留为最佳',
    en: 'Keep as best take',
    es: 'Conservar como mejor toma',
};
/** "Record another take" button label. */
export const RETAKE = {
    icon: '🔁',
    zh: '再录一次',
    en: 'Record again',
    es: 'Volver a grabar',
};
/** "Mark for review" placeholder (held button, not yet wired up). */
export const MARK_FOR_REVIEW = {
    icon: '🔖',
    zh: '标记给监导复核',
    en: 'Mark for supervisor review',
    es: 'Marcar para revisión del supervisor',
};
/**
 * Format a percent-deviation summary for the modal body.
 * @param percentSigned positive = take is longer than reference; negative = shorter
 */
export function variancePercentSummary(percentSigned) {
    const abs = Math.abs(percentSigned).toFixed(0);
    const longer = percentSigned > 0;
    const sign = longer ? '+' : '-';
    return {
        zh: `${sign}${abs}% ${longer ? '比参考声更长' : '比参考声更短'}`,
        en: `${sign}${abs}% ${longer ? 'longer than reference' : 'shorter than reference'}`,
        es: `${sign}${abs}% ${longer ? 'más larga que la referencia' : 'más corta que la referencia'}`,
    };
}
// ── Three-take nudge ────────────────────────────────────────────────────────
// Soft prompt shown after the actor has recorded 3 takes on a single cue
// without picking a "best" yet. Three options: pick best (highlights the take
// strip), record a 4th, or skip the cue.
export const THREE_TAKE_TITLE = {
    icon: '🎙️',
    zh: '已经三条了',
    en: 'You have 3 takes',
    es: 'Ya lleva 3 tomas',
};
export const THREE_TAKE_BODY = {
    zh: '挑一条作为最佳就可以往下走,或者再录第四条。',
    en: 'Pick a best take to move on, or record a fourth.',
    es: 'Elija la mejor toma para continuar, o grabe una cuarta.',
};
export const PICK_BEST = {
    icon: '⭐',
    zh: '挑最佳',
    en: 'Pick best take',
    es: 'Elegir la mejor toma',
};
export const RECORD_FOURTH = {
    icon: '🔁',
    zh: '再来一条',
    en: 'Record a 4th',
    es: 'Grabar una cuarta',
};
export const SKIP_CUE = {
    icon: '⏭️',
    zh: '跳过这条',
    en: 'Skip this cue',
    es: 'Saltar esta línea',
};
